import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPI, callAPIWithoutAuth } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';

export const fetchProductById = createAsyncThunk(
  'productDetail/fetchProductById',
  async ({ productId, user_id }, { rejectWithValue }) => {
    try {
      const response = await callAPIWithoutAuth(
        `${apiUrls.productDetail}`,
        { id: productId, user_id: user_id },
        "GET",
        {}
      );

      if (response?.data?.success) {
        return {
          product: response.data.data,
          relatedProducts: response.data.related_products || {},
        };
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to fetch product details');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch product details');
    }
  }
);

export const fetchRelatedProducts = createAsyncThunk(
  'productDetail/fetchRelatedProducts',
  async ({ productId, limit = 8 } = {}, { rejectWithValue }) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 400));
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch related products');
    }
  }
);

export const fetchProductReviews = createAsyncThunk(
  'productDetail/fetchProductReviews',
  async ({ productId, page = 1, limit = 10, sort = 'most_recent', search = '' }, { rejectWithValue }) => {
    try {
      const response = await callAPIWithoutAuth(
        `${apiUrls.productReviews}`,
        {
          product_id: productId,
          review_sort: sort,
          review_search: search
        },
        "GET",
        {}
      );

      if (response?.data?.success) {
        const responseData = response.data.data;

        return {
          reviews: responseData.data || [],
          totalReviews: responseData.total || 0,
          currentPage: responseData.current_page || page,
          itemsPerPage: responseData.per_page || limit,
          totalPages: responseData.last_page || 1,
          sortBy: sort,
          searchTerm: search,
          averageRating: response.data.average_rating || 0,
          recommendedCount: response.data.recommended_count || 0,
          recommendationPercentage: response.data.recommendation_percentage || 0,
          reviewsCount: response.data.reviews_count || 0
        };
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to fetch reviews');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch reviews');
    }
  }
);

export const submitProductReview = createAsyncThunk(
  'productDetail/submitProductReview',
  async (reviewData, { rejectWithValue, getState }) => {
    try {
      const response = await callAPI(
        apiUrls.addProductReview,
        {},
        "POST",
        reviewData
      );

      if (response.data.success) {
        const state = getState();
        const user = state.auth?.user;

        const newReview = {
          id: response.data.data.id,
          user: {
            id: response.data.data.user_id,
            full_name: user?.full_name || 'You',
            image_url: user?.image_url
          },
          rating: response.data.data.rating,
          comment: response.data.data.comment,
          created_at: response.data.data.created_at,
          helpful: 0,
          notHelpful: 0,
          images: [],
          verified_purchase: true
        };

        return newReview;
      }
      return rejectWithValue(response.data?.message || 'Failed to submit review');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit review');
    }
  }
);

export const markReviewHelpful = createAsyncThunk(
  'productDetail/markReviewHelpful',
  async ({ reviewId, helpful = true }, { rejectWithValue }) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 200));
      return { reviewId, helpful };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark review');
    }
  }
);

const productDetailSlice = createSlice({
  name: 'productDetail',
  initialState: {
    product: null,
    productId: null,
    selectedImage: 0,
    selectedVariant: null,
    quantity: 1,
    isFavorite: false,

    reviews: [],
    filteredReviews: [],
    totalReviews: 0,
    averageRating: 0,
    recommendedCount: 0,
    recommendationPercentage: 0,
    reviewsPage: 1,
    reviewsPerPage: 10,
    totalReviewPages: 1,
    reviewsSort: 'most_recent',
    reviewSearchTerm: '',

    relatedProducts: [],

    loading: false,
    productLoading: false,
    reviewsLoading: false,
    relatedLoading: false,
    submitLoading: false,
    error: null,
    reviewError: null,

    submitSuccess: false,
  },
  reducers: {
    setSelectedImage: (state, action) => {
      state.selectedImage = action.payload;
    },

    setSelectedVariant: (state, action) => {
      state.selectedVariant = action.payload;
      state.selectedImage = 0;
      state.quantity = 1;
    },

    setQuantity: (state, action) => {
      const maxStock = state.selectedVariant?.stock || state.product?.total_stock || Infinity;
      state.quantity = Math.max(1, Math.min(action.payload, maxStock));
    },

    incrementQuantity: (state) => {
      const maxStock = state.selectedVariant?.stock || state.product?.total_stock || Infinity;
      if (state.quantity < maxStock) {
        state.quantity += 1;
      }
    },

    decrementQuantity: (state) => {
      state.quantity = Math.max(1, state.quantity - 1);
    },
    resetQuantity: (state) => {
      state.quantity = 1;
    },

    toggleFavorite: (state) => {
      state.isFavorite = !state.isFavorite;
    },

    setIsFavorite: (state, action) => {
      state.isFavorite = action.payload;
    },

    setReviewSearchTerm: (state, action) => {
      state.reviewSearchTerm = action.payload;
      if (action.payload.trim() === '') {
        state.filteredReviews = state.reviews;
      } else {
        const searchLower = action.payload.toLowerCase();
        state.filteredReviews = state.reviews.filter(review =>
          review.comment?.toLowerCase().includes(searchLower) ||
          review.user?.full_name?.toLowerCase().includes(searchLower)
        );
      }
    },

    setReviewsSort: (state, action) => {
      state.reviewsSort = action.payload;
      state.reviewsPage = 1;

      if (state.filteredReviews.length > 0) {
        const sorted = [...state.filteredReviews];
        switch (action.payload) {
          case 'highest_rating':
            sorted.sort((a, b) => b.rating - a.rating);
            break;
          case 'lowest_rating':
            sorted.sort((a, b) => a.rating - b.rating);
            break;
          case 'most_recent':
          default:
            sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            break;
        }
        state.filteredReviews = sorted;
      }
    },

    setReviewsPage: (state, action) => {
      state.reviewsPage = action.payload;
    },

    resetProductState: (state) => {
      state.product = null;
      state.productId = null;
      state.selectedImage = 0;
      state.selectedVariant = null;
      state.quantity = 1;
      state.isFavorite = false;
      state.reviews = [];
      state.filteredReviews = [];
      state.relatedProducts = [];
      state.loading = false;
      state.error = null;
      state.reviewError = null;
    },

    clearProductDetailError: (state) => {
      state.error = null;
    },

    clearReviewError: (state) => {
      state.reviewError = null;
    },

    resetSubmitStatus: (state) => {
      state.submitSuccess = false;
    },

    updateRelatedProductFavoriteBatch: (state, action) => {
      state.relatedProducts = action.payload;
    },

    updateRelatedProductFavorite: (state, action) => {
      const { productId, isFavorite } = action.payload;
      state.relatedProducts = state.relatedProducts.map(product =>
        product.id === productId
          ? { ...product, isFavorite, is_saved: isFavorite }
          : product
      );
    },

    clearReviewFilters: (state) => {
      state.reviewSearchTerm = '';
      state.reviewsSort = 'most_recent';
      state.reviewsPage = 1;
      state.filteredReviews = state.reviews;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProductById.pending, (state) => {
        state.productLoading = true;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.productLoading = false;
        state.loading = false;
        state.product = action.payload.product;
        state.isFavorite = action.payload.product?.is_saved || false;
        state.selectedImage = 0;

        const product = action.payload.product;
        if (product.battery_variants?.length > 0) {
          state.selectedVariant = product.battery_variants[0];
        } else if (product.tyre_variants?.length > 0) {
          state.selectedVariant = product.tyre_variants[0];
        } else if (product.windshield_variants?.length > 0) {
          state.selectedVariant = product.windshield_variants[0];
        }

        const relatedData = action.payload.relatedProducts;
        const related = [];

        if (relatedData?.tyres?.length > 0) {
          relatedData.tyres.forEach(tyre => {
            if (tyre.id !== product.id) {
              const variant = tyre.tyre_variants?.[0];
              related.push({
                id: tyre.id,
                name: tyre.name,
                brand: tyre.brand?.name || 'Unknown',
                brandImage: tyre.brand?.image_url || 'Unknown',
                image: variant?.images?.[0]?.image_url || tyre.category.image_url,
                price: parseFloat(variant?.price || 0),
                discounted_price: parseFloat(variant?.discounted_price || variant?.price || 0),
                discount: variant?.price && variant?.discounted_price
                  ? Math.round(((parseFloat(variant.price) - parseFloat(variant.discounted_price)) / parseFloat(variant.price)) * 100)
                  : 0,
                reviews_count: tyre.reviews_count || 0,
                reviews_avg_rating: parseFloat(tyre.reviews_avg_rating) || 0,
                rating: parseFloat(tyre.reviews_avg_rating) || 4,
                reviews: tyre.reviews_count || 0,
                category: 'tyres',
                slug: tyre.slug,
                is_saved: tyre.is_saved || false,
                isFavorite: tyre.is_saved || false
              });
            }
          });
        }

        if (relatedData?.batteries?.length > 0) {
          relatedData.batteries.forEach(battery => {
            if (battery.id !== product.id) {
              const variant = battery.battery_variants?.[0];
              related.push({
                id: battery.id,
                name: battery.name,
                brand: battery.brand?.name || 'Unknown',
                brandImage: battery.brand.image_url,
                image: variant?.images?.[0]?.image_url || battery.category.image_url,
                price: parseFloat(variant?.price || 0),
                discounted_price: parseFloat(variant?.discounted_price || variant?.price || 0),
                discount: variant?.price && variant?.discounted_price
                  ? Math.round(((parseFloat(variant.price) - parseFloat(variant.discounted_price)) / parseFloat(variant.price)) * 100)
                  : 0,
                reviews_count: battery.reviews_count || 0,
                reviews_avg_rating: parseFloat(battery.reviews_avg_rating) || 0,
                rating: parseFloat(battery.reviews_avg_rating) || 4,
                reviews: battery.reviews_count || 0,
                category: 'batteries',
                slug: battery.slug,
                is_saved: battery.is_saved || false,
                isFavorite: battery.is_saved || false
              });
            }
          });
        }

        if (relatedData?.windshields?.length > 0) {
          relatedData.windshields.forEach(windshield => {
            if (windshield.id !== product.id) {
              const variant = windshield.windshield_variants?.[0];
              related.push({
                id: windshield.id,
                name: windshield.name,
                brand: windshield.brand?.name || 'Unknown',
                brandImage: windshield.brand?.image_url || 'Unknown',
                image: variant?.images?.[0]?.image_url || windshield.category.image_url,
                price: parseFloat(variant?.price || 0),
                discounted_price: parseFloat(variant?.discounted_price || variant?.price || 0),
                discount: variant?.price && variant?.discounted_price
                  ? Math.round(((parseFloat(variant.price) - parseFloat(variant.discounted_price)) / parseFloat(variant.price)) * 100)
                  : 0,
                reviews_count: windshield.reviews_count || 0,
                reviews_avg_rating: parseFloat(windshield.reviews_avg_rating) || 0,
                rating: parseFloat(windshield.reviews_avg_rating) || 0,
                reviews: windshield.reviews_count || 0,
                category: 'windshields',
                slug: windshield.slug,
                is_saved: windshield.is_saved || false,
                isFavorite: windshield.is_saved || false
              });
            }
          });
        }

        state.relatedProducts = related;
        state.relatedLoading = false;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.productLoading = false;
        state.loading = false;
        state.error = action.payload || 'Failed to fetch product details';
      })

      .addCase(fetchRelatedProducts.pending, (state) => {
        state.relatedLoading = true;
        state.error = null;
      })
      .addCase(fetchRelatedProducts.fulfilled, (state, action) => {
        state.relatedLoading = false;
        state.relatedProducts = action.payload;
      })
      .addCase(fetchRelatedProducts.rejected, (state, action) => {
        state.relatedLoading = false;
        state.error = action.payload || 'Failed to fetch related products';
      })

      .addCase(fetchProductReviews.pending, (state) => {
        state.reviewsLoading = true;
        state.reviewError = null;
      })
      .addCase(fetchProductReviews.fulfilled, (state, action) => {
        state.reviewsLoading = false;
        state.reviews = action.payload.reviews;
        state.filteredReviews = action.payload.reviews;
        state.totalReviews = action.payload.totalReviews;
        state.reviewsPage = action.payload.currentPage;
        state.reviewsPerPage = action.payload.itemsPerPage;
        state.totalReviewPages = action.payload.totalPages;
        state.reviewsSort = action.payload.sortBy;
        state.reviewSearchTerm = action.payload.searchTerm || '';
        state.averageRating = action.payload.averageRating || 0;
        state.recommendedCount = action.payload.recommendedCount || 0;
        state.recommendationPercentage = action.payload.recommendationPercentage || 0;
      })
      .addCase(fetchProductReviews.rejected, (state, action) => {
        state.reviewsLoading = false;
        state.reviewError = action.payload || 'Failed to fetch reviews';
      })

      .addCase(submitProductReview.pending, (state) => {
        state.submitLoading = true;
        state.submitSuccess = false;
        state.reviewError = null;
      })
      .addCase(submitProductReview.fulfilled, (state, action) => {
        state.submitLoading = false;
        state.submitSuccess = true;

        state.reviews = [action.payload, ...state.reviews];

        if (state.reviewSearchTerm.trim() === '') {
          state.filteredReviews = [action.payload, ...state.filteredReviews];
        } else {
          const searchLower = state.reviewSearchTerm.toLowerCase();
          const matchesSearch = action.payload.comment?.toLowerCase().includes(searchLower) ||
            action.payload.user?.full_name?.toLowerCase().includes(searchLower);
          if (matchesSearch) {
            state.filteredReviews = [action.payload, ...state.filteredReviews];
          }
        }

        state.totalReviews += 1;

        const sum = state.reviews.reduce((acc, review) => acc + review.rating, 0);
        state.averageRating = sum / state.reviews.length;
      })
      .addCase(submitProductReview.rejected, (state, action) => {
        state.submitLoading = false;
        state.submitSuccess = false;
        state.reviewError = action.payload || 'Failed to submit review';
      })

      .addCase(markReviewHelpful.fulfilled, (state, action) => {
        const { reviewId, helpful } = action.payload;

        const updateReview = (review) => {
          if (review.id === reviewId) {
            if (helpful) {
              review.helpful = (review.helpful || 0) + 1;
            } else {
              review.notHelpful = (review.notHelpful || 0) + 1;
            }
          }
        };

        state.reviews.forEach(updateReview);
        state.filteredReviews.forEach(updateReview);
      });
  }
});

export const {
  setSelectedImage,
  setSelectedVariant,
  setQuantity,
  incrementQuantity,
  decrementQuantity,
  toggleFavorite,
  setIsFavorite,
  setReviewSearchTerm,
  setReviewsSort,
  setReviewsPage,
  resetProductState,
  clearProductDetailError,
  clearReviewError,
  clearReviewFilters,
  updateRelatedProductFavoriteBatch,
  resetSubmitStatus,
  resetQuantity,
  updateRelatedProductFavorite
} = productDetailSlice.actions;

export default productDetailSlice.reducer;